import React from 'react';
import { Box, Typography, Grid, Paper, Container, Card, CardContent as MuiCardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomButton from './common/CustomButton';

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#F8FAFC',
  marginTop: 0,
  paddingBottom: '4rem'
}));

const HeaderContainer = styled(Grid)(({ theme }) => ({
  marginBottom: '4rem',
  paddingTop: '4rem'
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  color: '#1A365D',
  marginBottom: '1rem',
  '& span': {
    display: 'block'
  }
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#475569',
  lineHeight: 1.7,
  marginBottom: '2rem'
}));

const OrganizationCard = styled(Card)({
  height: '100%',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E2E8F0',
  borderRadius: '2px',
  boxShadow: 'none',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    backgroundColor: props => props.bordercolor || '#1A365D',
    transition: 'height 0.3s ease'
  },
  '&:hover::before': {
    height: '100%'
  }
});

const StyledCardContent = styled(MuiCardContent)({
  padding: '2rem',
  position: 'relative',
  backgroundColor: '#FFFFFF',
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: '#F8FAFC'
  }
});

const OrganizationTitle = styled(Typography)({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: props => props.color || '#1A365D',
  marginBottom: '1.5rem',
  position: 'relative',
  paddingBottom: '0.75rem',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '2rem',
    height: '2px',
    backgroundColor: props => props.color || '#1A365D'
  }
});

const OrganizationDescription = styled(Typography)({
  fontSize: '0.95rem',
  color: '#475569',
  lineHeight: 1.7
});

const AnimatedArrow = styled(Box)({
  width: '24px',
  height: '24px',
  borderRight: '2px solid #1A365D',
  borderBottom: '2px solid #1A365D',
  transform: 'rotate(45deg)',
  animation: 'bounce 2s infinite',
  '@keyframes bounce': {
    '0%, 20%, 50%, 80%, 100%': {
      transform: 'translateY(0) rotate(45deg)'
    },
    '40%': {
      transform: 'translateY(-10px) rotate(45deg)'
    },
    '60%': {
      transform: 'translateY(-5px) rotate(45deg)'
    }
  }
});

const organizations = [
  {
    name: 'ILAC',
    color: '#1A365D',
    description: "L'ILAC est l'organisation internationale des organismes d'accréditation, opérant selon la norme ISO/CEI 17011. Elle accorde l'accréditation aux laboratoires d'étalonnage, d'essais, d'analyses médicales et aux organismes d'inspection. L'accréditation garantit l'impartialité et la compétence des organismes d'évaluation de la conformité, de faire confiance aux résultats d'étalonnage, de mesure, d'essais et rapports d'inspection et certificats délivrés."
  },
  {
    name: 'TUNAC',
    color: '#C53030',
    description: "Le TUNAC est un organisme d'accréditation en Tunisie, sous la tutelle du Ministère de l'Industrie. Le TUNAC est membre des accords de reconnaissance multilatéraux (MLA) au sein de l'EA, de l'ILAC et de l'IAF. Ces accords assurent la reconnaissance mutuelle des accréditations, facilitant ainsi la reconnaissance internationale des certificats délivrés par les organismes accrédités par le TUNAC."
  },
  {
    name: 'COFRAC',
    color: '#2B6CB0',
    description: "Le Cofrac est l'unique organisme d'accréditation en France, reconnu par l'État, garantissant la compétence et l'impartialité des organismes d'évaluation de la conformité. Il est signataire des accords multilatéraux (MLA) avec des organisations internationales, permettant la reconnaissance internationale des certifications émises par les organismes accrédités."
  },
  {
    name: 'SEMAC',
    color: '#2F855A',
    description: "Le SEMAC est l'organisme marocain d'accréditation sous la tutelle du Ministère de l'Industrie et du Commerce. Il est représenté par la division d'accréditation au sein de la DPCSMQ. Il s'agit d'un organisme gouvernemental, couvert par le gouvernement en matière de responsabilités juridiques, et son activité d'accréditation est à but non lucratif."
  }
];

const AccreditationsInfo = ({ onScrollToList }) => {
  return (
    <SectionContainer>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 8, lg: 12 } }}>
        <HeaderContainer container spacing={4}>
          <Grid item xs={12} md={5}>
            <Title>
              <span>Nous disposons</span>
              <span>d'accréditations</span>
              <span>ISO/IEC 17025v2017</span>
            </Title>
          </Grid>
          <Grid item xs={12} md={7}>
            <Description>
              Afin d'apporter à nos clients le meilleur niveau de service dans le domaine de la métrologie dans l'optique de contribuer à l'amélioration de son processus de mesure, le laboratoire L2MI  depuis 2005 ainsi que le laboratoire AIR METROLOGIE depuis 2007 se sont engagé dans une demarche qualité et de conformité des prestations attendus par le client, de renforcer la fiabilité et la rigueur de son fonctionnement et de s'ameliorer de façon continue. Nos prestations sont accréditées par les organismes suivants:
            </Description>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, mb: 4 }}>
              <Box sx={{ position: 'relative', width: 'fit-content' }}>
                <CustomButton onClick={onScrollToList}>
                  Voir nos accréditations
                </CustomButton>
                <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', mt: 2 }}>
                  <AnimatedArrow />
                </Box>
              </Box>
            </Box>
          </Grid>
        </HeaderContainer>
        
        <Grid container spacing={3}>
          {organizations.map((org, index) => (
            <Grid item xs={12} sm={6} key={org.name}>
              <OrganizationCard bordercolor={org.color}>
                <StyledCardContent>
                  <OrganizationTitle color={org.color}>
                    {org.name}
                  </OrganizationTitle>
                  <OrganizationDescription>
                    {org.description}
                  </OrganizationDescription>
                </StyledCardContent>
              </OrganizationCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </SectionContainer>
  );
};

export default AccreditationsInfo;
